// src/pages/Dashboard.js
import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAgents } from '../hooks/useAgents';
import AgentCard from '../components/AgentCard';
import AgentModal from '../components/AgentModal';
import FrameModal from '../components/FrameModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { agents, statuses, loading, addAgent, updateAgent, deleteAgent, refreshAgent } = useAgents();

  const [agentModal, setAgentModal] = useState({ open: false, agent: null });
  const [frameModal, setFrameModal] = useState({ open: false, url: '', title: '' });
  const [deleteModal, setDeleteModal] = useState({ open: false, agent: null });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | connected | offline

  // Summary stats
  // whatsapp_ready: true  → connected (login আছে)
  // whatsapp_ready: false → offline (logout বা QR scan হয়নি)
  // s === undefined       → server reach হয়নি
  const stats = useMemo(() => {
    let connected = 0, offline = 0;
    agents.forEach(a => {
      const s = statuses[a.id];
      if (s?.whatsapp_ready) connected++;
      else offline++;
    });
    return { total: agents.length, connected, offline };
  }, [agents, statuses]);

  const filtered = useMemo(() => {
    return agents.filter(a => {
      const matchSearch = !search ||
        a.name?.toLowerCase().includes(search.toLowerCase()) ||
        a.domain?.toLowerCase().includes(search.toLowerCase());

      const s = statuses[a.id];
      const matchFilter =
        filter === 'all' ||
        (filter === 'connected' && s?.whatsapp_ready === true) ||
        (filter === 'offline' && !s?.whatsapp_ready);

      return matchSearch && matchFilter;
    });
  }, [agents, statuses, search, filter]);

  const handleSave = async (form) => {
    if (agentModal.agent) {
      await updateAgent(agentModal.agent.id, form);
    } else {
      await addAgent(form);
    }
  };

  const handleDelete = async (agent) => {
    await deleteAgent(agent.id);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar */}
      <nav className="sticky top-0 z-30 card-glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          {/* Logo */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #15803d, #22c55e)' }}>
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.093.54 4.059 1.485 5.773L0 24l6.405-1.47A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.88 0-3.645-.49-5.178-1.348l-.371-.22-3.8.872.942-3.706-.242-.382A9.946 9.946 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
              </svg>
            </div>
            <span className="font-display font-700 text-white text-sm">WA Dashboard</span>
          </div>

          {/* Search */}
          <div className="flex-1 max-w-xs hidden sm:block">
            <div className="relative">
              <svg viewBox="0 0 24 24" className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search agents..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 rounded-xl text-sm font-body text-white placeholder-slate-600 outline-none"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
              />
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAgentModal({ open: true, agent: null })}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl font-display font-600 text-xs text-white transition-all"
              style={{ background: 'linear-gradient(135deg, #15803d, #22c55e)' }}
            >
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              <span className="hidden sm:inline">Add Agent</span>
            </button>

            {/* User avatar */}
            <div className="flex items-center gap-2 pl-2 border-l border-white/10">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-display font-700 text-white"
                style={{ background: 'linear-gradient(135deg, #1e40af, #3b82f6)' }}>
                {user?.email?.[0]?.toUpperCase() || 'A'}
              </div>
              <button
                onClick={logout}
                className="text-slate-500 hover:text-white transition-colors"
                title="Logout"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6">
        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <StatCard label="Total Agents" value={stats.total} color="slate" />
          <StatCard label="Connected" value={stats.connected} color="green" />
          <StatCard label="Offline" value={stats.offline} color="red" />
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1">
          {[
            { key: 'all', label: 'All' },
            { key: 'connected', label: '● Connected' },
            { key: 'offline', label: '● Offline' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-display font-600 whitespace-nowrap transition-all ${
                filter === t.key
                  ? 'text-white'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
              style={filter === t.key
                ? { background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)' }
                : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }
              }
            >
              {t.label}
            </button>
          ))}
          {/* Mobile search */}
          <div className="sm:hidden flex-1 min-w-32">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 rounded-xl text-xs font-body text-white placeholder-slate-600 outline-none"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <svg className="animate-spin w-6 h-6 mr-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            Loading agents...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-600">
            <svg viewBox="0 0 24 24" className="w-10 h-10 mb-4 opacity-40" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            <p className="font-display font-600 text-sm mb-1">No agents found</p>
            <p className="text-xs">
              {agents.length === 0 ? 'Add your first agent to get started' : 'Try adjusting your search or filter'}
            </p>
            {agents.length === 0 && (
              <button
                onClick={() => setAgentModal({ open: true, agent: null })}
                className="mt-4 px-4 py-2 rounded-xl font-display font-600 text-sm text-white"
                style={{ background: 'linear-gradient(135deg, #15803d, #22c55e)' }}
              >
                Add First Agent
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((agent, i) => (
              <div key={agent.id} className="animate-slide-up" style={{ animationDelay: `${i * 40}ms` }}>
                <AgentCard
                  agent={agent}
                  status={statuses[agent.id]}
                  onRefresh={refreshAgent}
                  onEdit={(a) => setAgentModal({ open: true, agent: a })}
                  onDelete={(a) => setDeleteModal({ open: true, agent: a })}
                  onOpenFrame={(url, title) => setFrameModal({ open: true, url, title })}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modals */}
      <AgentModal
        open={agentModal.open}
        agent={agentModal.agent}
        onClose={() => setAgentModal({ open: false, agent: null })}
        onSave={handleSave}
      />
      <FrameModal
        open={frameModal.open}
        url={frameModal.url}
        title={frameModal.title}
        onClose={() => setFrameModal({ open: false, url: '', title: '' })}
      />
      <DeleteConfirmModal
        open={deleteModal.open}
        agent={deleteModal.agent}
        onClose={() => setDeleteModal({ open: false, agent: null })}
        onConfirm={handleDelete}
      />
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colors = {
    slate: 'text-slate-300',
    green: 'text-brand-400',
    yellow: 'text-yellow-400',
    red: 'text-red-400',
  };
  return (
    <div className="card-glass rounded-xl px-4 py-3">
      <p className="text-slate-600 text-xs font-display uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-2xl font-display font-700 ${colors[color]}`}>{value}</p>
    </div>
  );
}